/**
 * Engage › Celebrations: an org-wide feed of upcoming birthdays and work anniversaries, plus a
 * deliberately lightweight "wish" — one click, a fixed emoji, an optional short note. No points, no
 * mandatory message, no `recognition:give` permission gate: this is NOT routed through
 * `recognition.contract.ts`'s kudos wall on purpose — a birthday wish is not performance recognition,
 * and forcing it through a points ledger would be the wrong shape for "just saying happy birthday."
 *
 * Work anniversaries read `Employment.joinedOn` live — it is already plaintext. Birthdays read a
 * daily-refreshed cache (`PersonBirthdayCache`, month/day only, no year) because `Person.dobCiphertext`
 * is encrypted PII with no blind index (CLAUDE.md invariant 7): decrypting every employee's DOB on
 * every page view is the thing that cache exists to avoid. See apps/worker's
 * `workers/celebrations.ts` for the sweep (it also sends the "today" notifications — see its own
 * header) and apps/api's `routes/celebrations.ts` for the read/write.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { dateOnlySchema, errorBodySchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const CELEBRATION_RANGES = ['week', 'month', 'quarter'] as const;
export const celebrationRangeSchema = z.enum(CELEBRATION_RANGES);
export type CelebrationRange = z.infer<typeof celebrationRangeSchema>;

export const CELEBRATION_KINDS = ['birthday', 'work_anniversary'] as const;
export const celebrationKindSchema = z.enum(CELEBRATION_KINDS);
export type CelebrationKind = z.infer<typeof celebrationKindSchema>;

/** A manager's own direct reports, one level deep — same scope `resolveDirectReportPersonIds` gives
 *  every other "My Team" toggle in the app. Empty for an individual contributor, not an error. */
export const CELEBRATION_SCOPES = ['org', 'team'] as const;
export const celebrationScopeSchema = z.enum(CELEBRATION_SCOPES);
export type CelebrationScope = z.infer<typeof celebrationScopeSchema>;

/** A fixed palette, not free emoji — same reasoning as `AnnouncementReaction`'s fixed enum. */
export const CELEBRATION_WISH_EMOJIS = ['🎉', '🎂', '🥳', '👏', '❤️'] as const;
export const celebrationWishEmojiSchema = z.enum(CELEBRATION_WISH_EMOJIS);
export type CelebrationWishEmoji = z.infer<typeof celebrationWishEmojiSchema>;

export const celebrationWishSchema = z.object({
  fromPersonId: uuidSchema,
  fromName: z.string(),
  emoji: celebrationWishEmojiSchema,
  message: z.string().nullable(),
});
export type CelebrationWish = z.infer<typeof celebrationWishSchema>;

export const celebrationEntrySchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  /** Free-text job title (`EmploymentAssignment.designation`) — null when never set. */
  designation: z.string().nullable(),
  kind: celebrationKindSchema,
  /** This year's (or next year's, if this year's has passed) occurrence. */
  occursOn: dateOnlySchema,
  /** Only set for `work_anniversary` — full years of service as of `occursOn`. */
  yearsOfService: z.number().int().positive().nullable(),
  wishCount: z.number().int().nonnegative(),
  /** Up to a handful of the most recent wishes, for "Priya, Arjun and 3 others wished them". */
  recentWishes: z.array(celebrationWishSchema),
  /** The viewer's own wish on this occurrence, if they have already sent one — null otherwise. */
  myWish: celebrationWishSchema.nullable(),
});
export type CelebrationEntry = z.infer<typeof celebrationEntrySchema>;

const listQuerySchema = z.object({
  /** How far ahead to look: a week, a month, or a quarter from today. Defaults to the narrowest. */
  range: celebrationRangeSchema.default('week'),
  kind: celebrationKindSchema.optional(),
  departmentId: uuidSchema.optional(),
  scope: celebrationScopeSchema.default('org'),
});

const sendWishBodySchema = z.object({
  personId: uuidSchema,
  kind: celebrationKindSchema,
  occursOn: dateOnlySchema,
  emoji: celebrationWishEmojiSchema,
  message: z.string().trim().max(280).optional(),
});

export const celebrationsContract = c.router(
  {
    listCelebrations: {
      method: 'GET',
      path: '/',
      query: listQuerySchema,
      responses: {
        200: z.object({ items: z.array(celebrationEntrySchema) }),
        401: errorBodySchema,
      },
      summary: 'Upcoming birthdays and work anniversaries, org-wide or just my team',
      metadata: routeMeta(null),
    },
    sendCelebrationWish: {
      method: 'PUT',
      path: '/wishes',
      body: sendWishBodySchema,
      responses: {
        200: celebrationEntrySchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Send (or update) my one-click wish on a specific birthday/anniversary occurrence',
      metadata: routeMeta(null),
    },
    getMyBirthdayVisibility: {
      method: 'GET',
      path: '/me/birthday-visibility',
      responses: {
        200: z.object({ hideBirthday: z.boolean() }),
        401: errorBodySchema,
      },
      summary: 'Whether my birthday is hidden from the Celebrations feed',
      metadata: routeMeta(null),
    },
    setMyBirthdayVisibility: {
      method: 'PUT',
      path: '/me/birthday-visibility',
      body: z.object({ hideBirthday: z.boolean() }),
      responses: {
        200: z.object({ hideBirthday: z.boolean() }),
        401: errorBodySchema,
      },
      summary: 'Show or hide my birthday on the Celebrations feed and in the daily digest',
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/celebrations' },
);
